import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Clock, Users, AlertCircle, ChevronLeft, ChevronRight, LayoutGrid } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StaffBadge, EventStatusBadge, ProgressLine, ProgressRing } from "@/components/atlas/Shared";
import { useAssignFlow } from "@/components/staffing/useAssignFlow";
import { NotifyTeamButton } from "@/components/staffing/notify";
import { EventInfo } from "@/components/atlas/EventInfo";
import { PositionCard } from "@/components/staffing/PositionCard";
import { CandidatePool } from "@/components/staffing/CandidatePool";
import { EventActions } from "@/components/staffing/EventActions";
import { EventNotifications } from "@/components/staffing/EventNotifications";
import { EventDocumentsAdmin } from "@/components/documents/EventDocumentsAdmin";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/positions";
import { useLang, errorText } from "@/lib/i18n";
import { fmtDate, fmtDateTime, fmtRange, fmtTime } from "@/lib/format";

const History = ({ eventId }) => {
  const { t, lang } = useLang();
  const { data } = useFetch(`/events/${eventId}/history`);
  if (!data) return null;
  if (!data.length) return <p className="text-sm text-neutral-500">{t("h.empty")}</p>;
  return (
    <ol className="card-lux divide-y divide-black/[0.05]" data-testid="event-history">
      {data.map((h) => (
        <li key={h.id} className="px-5 py-3 grid grid-cols-[120px_1fr] md:grid-cols-[150px_200px_1fr] gap-3 text-sm">
          <span className="mono text-xs text-neutral-400">{fmtDateTime(h.created_at, lang)}</span>
          <span className="font-medium">{t(`h.${h.action}`)}</span>
          <span className="text-neutral-600 col-span-2 md:col-span-1">{h.details} <span className="text-neutral-400">— {h.actor_name}</span></span>
        </li>
      ))}
    </ol>
  );
};

const EventNav = ({ id }) => {
  const { t } = useLang();
  const { data } = useFetch(`/events/${id}/neighbors`);
  const btn = "press inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs hover:border-[#C5A059]/60 transition-colors";
  if (!data) return null;
  return (
    <div className="flex items-center gap-2" data-testid="event-nav">
      {data.prev ? <Link to={`/admin/events/${data.prev.id}`} className={btn} data-testid="event-prev" title={data.prev.name}><ChevronLeft className="w-3.5 h-3.5" />{t("ev.prev")}</Link>
        : <span className={`${btn} opacity-30`}><ChevronLeft className="w-3.5 h-3.5" />{t("ev.prev")}</span>}
      <span className="mono text-[11px] text-neutral-500" data-testid="event-nav-index">{data.index}/{data.total}</span>
      {data.next ? <Link to={`/admin/events/${data.next.id}`} className={btn} data-testid="event-next" title={data.next.name}>{t("ev.next")}<ChevronRight className="w-3.5 h-3.5" /></Link>
        : <span className={`${btn} opacity-30`}>{t("ev.next")}<ChevronRight className="w-3.5 h-3.5" /></span>}
    </div>
  );
};

export default function EventBoard() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [board, setBoard] = useState(null);
  const [selected, setSelected] = useState(null);
  const [picked, setPicked] = useState([]);

  const load = useCallback(() => api.get(`/events/${id}/board`).then((r) => setBoard(r.data)), [id]);
  const flow = useAssignFlow(load);
  useEffect(() => {
    setBoard(null);
    setSelected(null);
    setPicked([]);
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  const pick = (aid) => setPicked((p) => (p.includes(aid) ? p.filter((x) => x !== aid) : [...p, aid]));
  const assign = (slot, candidate) => {
    flow.run((f) => api.post("/assignments", { event_id: id, slot_id: slot.id, employee_id: candidate.id, ...f }), t("b.assignedOk"), candidate.name);
  };
  const move = (slot, assignmentId) => {
    const all = board.slots.flatMap((s) => s.assignments);
    const a = all.find((x) => x.id === assignmentId);
    if (!a || a.slot_id === slot.id) return;
    flow.run((f) => api.patch(`/assignments/${assignmentId}`, { slot_id: slot.id, ...f }), t("b.movedOk"), a.employee_name);
  };
  const onDrop = (slot, item) => {
    if (item.type === "candidate") {
      const c = board.candidates.find((x) => x.id === item.id);
      if (c) assign(slot, c);
    } else if (item.type === "assignment") move(slot, item.id);
  };
  const remove = async (a) => {
    try {
      await api.delete(`/assignments/${a.id}`);
      toast.success(t("b.removedOk"));
      load();
    } catch (e) {
      toast.error(errorText(t, e));
    }
  };

  if (!board) return <p className="text-sm text-neutral-400">{t("c.loading")}</p>;
  const ev = board.event;
  const slot = board.slots.find((s) => s.id === selected);
  const disabled = ev.status === "cancelled";
  const activeIds = board.slots.flatMap((s) => s.assignments).filter((a) => a.status !== "declined").map((a) => a.id);
  const pickedLive = picked.filter((x) => activeIds.includes(x));

  return (
    <div data-testid="event-board-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <Link to="/admin/events" className="inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-black" data-testid="back-to-events"><ArrowLeft className="w-3.5 h-3.5" />{t("nav.events")}</Link>
        <div className="flex flex-wrap items-center gap-3">
          <Link to={`/admin/day/${ev.date}`} className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-[#D9B872]" data-testid="open-day-board"><LayoutGrid className="w-3.5 h-3.5" />{t("day.open")}</Link>
          <EventNav id={id} />
        </div>
      </div>
      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-6 mb-8 rise">
        <div className="min-w-0 flex items-start gap-6">
          <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <EventStatusBadge status={ev.status} testid="event-status-badge" />
            <StaffBadge status={ev.stats.staffing_status} testid="event-staffing-badge" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-none" data-testid="event-name">{ev.name}</h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-neutral-600">
            <span className="capitalize">{fmtDate(ev.date, lang)}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#C5A059]" /><span className="mono">{fmtRange(ev.start_time, ev.end_time, t)}</span>{ev.report_time && <span className="text-neutral-400">· {t("c.reporting")} {fmtTime(ev.report_time, t)}</span>}</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#C5A059]" />{ev.location}{ev.address ? ` — ${ev.address}` : ""}</span>
            {ev.client && <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#C5A059]" />{ev.client}</span>}
          </div>
          </div>
          <ProgressRing filled={ev.stats.filled} required={ev.stats.required} label={lang === "en" ? "positions filled" : "postes pourvus"} testid="event-progress-ring" />
        </div>
        <div className="flex flex-wrap gap-2"><EventActions event={ev} reload={load} /></div>
      </div>

      {ev.status === "draft" && <div className="mb-6 px-4 py-3 rounded-xl border border-dashed border-white/15 text-sm text-neutral-600" data-testid="draft-note">{t("b.draftNote")}</div>}
      {ev.status === "cancelled" && <div className="mb-6 px-4 py-3 border border-red-200 bg-red-50 text-sm text-red-700" data-testid="cancelled-note">{t("b.cancelledNote")}</div>}

      <div className="card-lux p-5 mb-8 grid md:grid-cols-[1fr_auto] gap-4 items-center" data-testid="overall-progress">
        <div>
          <div className="eyebrow mb-2">{t("b.overall")}</div>
          <ProgressLine filled={ev.stats.filled} required={ev.stats.required} testid="overall-progress-bar" />
        </div>
        <div className="flex gap-6 text-xs">
          <span><span className="mono text-lg text-black mr-1">{ev.stats.unfilled}</span>{t("b.remaining")}</span>
          <span><span className="mono text-lg text-indigo-700 mr-1">{ev.stats.pending}</span>{t("as.pending")}</span>
          <span><span className="mono text-lg text-emerald-700 mr-1">{ev.stats.confirmed}</span>{t("as.confirmed")}</span>
        </div>
      </div>

      <Tabs defaultValue="board">
        <TabsList className="rounded-none bg-transparent border-b border-black/10 w-full justify-start h-auto p-0 mb-6 gap-6 overflow-x-auto">
          {["board", "details", "notifications", "history"].map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`event-tab-${k}`} className="rounded-none px-0 pb-3 data-[state=active]:shadow-none data-[state=active]:bg-transparent border-b-2 border-transparent data-[state=active]:border-[#C5A059] text-sm">{t(`b.${k}`)}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="board">
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="notify-toolbar">
            <NotifyTeamButton eventId={id} count={activeIds.length} reload={load} />
            <NotifyTeamButton eventId={id} count={pickedLive.length} ids={pickedLive} reload={() => { setPicked([]); load(); }} testid="notify-selected-btn" label={t("nt.selected")} />
            {board.removed_pending?.length > 0 && <NotifyTeamButton eventId={id} count={board.removed_pending.length} ids={board.removed_pending.map((r) => r.id)} reload={load} testid="notify-removed-btn" label={t("nt.removed")} />}
            <span className="text-xs text-neutral-500">{t("nt.manualHint")}</span>
          </div>
          <div className="grid xl:grid-cols-[1fr_360px] gap-6">
            <div className="grid md:grid-cols-2 gap-4 content-start" data-testid="positions-grid">
              {board.slots.map((s) => (
                <PositionCard key={s.id} slot={s} event={ev} selected={s.id === selected} onSelect={setSelected} onDropItem={onDrop} onRemove={remove} disabled={disabled}
                  picked={pickedLive} onPick={pick} reload={load}
                  onAssign={assign} available={board.candidates.filter((c) => ["available", "maybe"].includes(c.availability) && c.interested_slots.includes(s.id) && !c.assigned_slot_ids.includes(s.id))
                    .sort((a, b) => (a.availability === b.availability ? a.name.localeCompare(b.name) : a.availability === "available" ? -1 : 1))} />
              ))}
            </div>
            <div className="xl:sticky xl:top-20 self-start">
              <CandidatePool candidates={board.candidates} slot={slot} onAssign={(c) => slot && assign(slot, c)} disabled={disabled} />
            </div>
          </div>
        </TabsContent>
        <TabsContent value="details">
          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <div className="min-w-0 space-y-6"><div className="card-lux p-6 md:p-8"><EventInfo event={ev} showInternal /></div><EventDocumentsAdmin eventId={id} /></div>
            <div className="space-y-4">
              {ev.availability_deadline && (
                <div className="card-lux p-6">
                  <div className="eyebrow mb-2">{t("f.deadline")}</div><div className="text-sm capitalize">{fmtDate(ev.availability_deadline, lang)}</div>
                </div>
              )}
              {ev.issues?.length > 0 && (
                <div className="card-lux p-6" data-testid="event-issues">
                  <div className="eyebrow mb-3 flex items-center gap-2"><AlertCircle className="w-3.5 h-3.5 text-red-500" />{t("b.issues")}</div>
                  {ev.issues.map((i) => <div key={i.id} className="text-sm py-2 border-b last:border-0"><p>{i.text}</p><p className="text-[11px] text-neutral-400 mt-1">{i.by} · {fmtDateTime(i.created_at, lang)}</p></div>)}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="notifications"><EventNotifications eventId={id} /></TabsContent>
        <TabsContent value="history"><History eventId={id} /></TabsContent>
      </Tabs>

      {flow.dialog}
    </div>
  );
}
